import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  message: string;
  variant?: ToastVariant;
  duration?: number; // ms; default 5000
}

interface ToastItem extends Required<ToastOptions> {
  id: string;
}

interface ToastApi {
  notify: (opts: ToastOptions) => string; // returns the id
  dismiss: (id: string) => void;
}

const Ctx = createContext<ToastApi | undefined>(undefined);

export function useToast(): ToastApi {
  const api = useContext(Ctx);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback(({ message, variant = 'info', duration = 5000 }: ToastOptions) => {
    const id = `t-${++nextId}`;
    setItems((prev) => [...prev, { id, message, variant, duration }]);
    return id;
  }, []);

  const api = useMemo<ToastApi>(() => ({ notify, dismiss }), [notify, dismiss]);

  return (
    <Ctx.Provider value={api}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </Ctx.Provider>
  );
}

const VARIANT: Record<
  ToastVariant,
  { icon: typeof Info; ring: string; bg: string; iconCls: string }
> = {
  success: {
    icon: CheckCircle2,
    ring: 'border-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-950/40',
    iconCls: 'text-emerald-600',
  },
  error: {
    icon: XCircle,
    ring: 'border-rose-500',
    bg: 'bg-rose-50 dark:bg-rose-950/40',
    iconCls: 'text-rose-600',
  },
  warning: {
    icon: AlertTriangle,
    ring: 'border-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    iconCls: 'text-amber-600',
  },
  info: {
    icon: Info,
    ring: 'border-brand-500',
    bg: 'bg-brand-50 dark:bg-brand-900/40',
    iconCls: 'text-brand-700 dark:text-brand-400',
  },
};

function ToastViewport({
  items,
  onDismiss,
}: {
  items: ToastItem[];
  onDismiss: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return createPortal(
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed z-[60] top-4 right-4 left-4 sm:left-auto flex flex-col gap-2 items-stretch sm:items-end pointer-events-none"
    >
      {items.map((t) => (
        <ToastItemView key={t.id} item={t} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  );
}

function ToastItemView({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (id: string) => void;
}) {
  const v = VARIANT[item.variant];
  const Icon = v.icon;

  useEffect(() => {
    if (item.duration <= 0) return;
    const id = window.setTimeout(() => onDismiss(item.id), item.duration);
    return () => window.clearTimeout(id);
  }, [item.id, item.duration, onDismiss]);

  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto w-full sm:w-auto sm:min-w-[320px] sm:max-w-md',
        'flex items-start gap-3 px-4 py-3 rounded-xl shadow-lg border-l-4',
        v.ring,
        v.bg,
        'text-stone-900 dark:text-stone-100',
        'animate-in slide-in-from-right-4 fade-in duration-200',
      )}
    >
      <Icon className={cn('h-5 w-5 mt-0.5 flex-shrink-0', v.iconCls)} aria-hidden="true" />
      <p className="flex-1 text-sm leading-snug">{item.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        aria-label="Dismiss notification"
        className="-mr-1 p-1 min-h-tap min-w-tap rounded text-stone-500 hover:text-stone-900 hover:bg-black/5 dark:hover:bg-white/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
